import { useHudStore } from "../store/hudStore";
import { ACC_META, peso } from "../data/seed";
import { Badge, Card } from "../design-system/components";
import "./financeDetail.css";
import Page from "../components/Page";
import { isReceivablePayment } from "../lib/financeAnalytics";

// Account detail: the money in and out of a single account, newest first. Opened
// from an account card in the Finances module (FinanceModule -> st.openAccount2).
// Every income, expense and transfer that touches the account is folded into one
// signed list, so "where did this balance come from and go" reads at a glance -
// the account-level counterpart to the debt page's payment history.

const CUR_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// A ledger row stores a display date ("Aug 22") with no year; a month*100+day key
// orders the list newest-first while it sits inside one year. Same approach as the
// module's ledger (components/modules/FinanceModule.tsx).
const dateOrd = (d: string): number => {
  const m = /^([A-Za-z]{3})[a-z]*\s+(\d{1,2})/.exec(String(d ?? ""));
  const mi = m ? CUR_MONTHS.indexOf(m[1]!) : -1;
  return mi < 0 ? -1 : mi * 100 + Number(m![2]);
};

const BANK = { fa: "fa-solid fa-building-columns", color: "var(--mut)", tint: "var(--card2)" };

type Row = { date: string; label: string; sub: string; amount: number };

export default function Account() {
  const st = useHudStore();
  const a = st.accounts.find((x) => x.id === st.accountId);

  // Resolve an account or debt id to a readable name, for a transfer's other side.
  const nameOf = (id: string): string => {
    if (!id) return "Not set";
    const acc = st.accounts.find((x) => x.id === id);
    if (acc) return acc.name;
    const d = st.debts.find((x) => x.id === id);
    return d ? d.name : id;
  };

  if (!a) {
    return (
      <Page back="Back to Finances" onBack={st.closeAccount2}>
        <Card><h1 className="detail-heading detail-title">That account is gone.</h1></Card>
      </Page>
    );
  }

  const m = ACC_META[a.type] || BANK;

  // Every ledger row that touches this account, as a signed movement. A transfer
  // shows on both of its accounts, once out of `from` and once into `to`.
  const rows: Row[] = [];
  for (const i of st.incomes) if (i.account === a.id) rows.push({ date: i.date, label: i.source || "Income", sub: "Income", amount: i.amount });
  for (const e of st.expenses) if (e.account === a.id) rows.push({ date: e.date, label: e.note || "Expense", sub: e.cat || "Expense", amount: -e.amount });
  for (const t of st.transfers) {
    if (t.from === a.id) rows.push({ date: t.date, label: t.note || `To ${nameOf(t.to)}`, sub: `Transfer to ${nameOf(t.to)}`, amount: -t.amount });
    if (t.to === a.id) rows.push({ date: t.date, label: t.note || `From ${nameOf(t.from)}`, sub: `Transfer from ${nameOf(t.from)}`, amount: t.amount });
  }
  // Debt payments made from this account. log_payment/loan_payment debit the
  // account with no ledger row of their own, so without this the money that went
  // to a debt would leave the balance yet show nowhere. The stored date carries a
  // year ("Aug 26, 2026"); trim it to match the ledger's short form.
  for (const p of st.debtPayments) if (p.account === a.id && !isReceivablePayment(p, st.debts)) rows.push({ date: String(p.date).replace(/,?\s*\d{4}$/, ""), label: p.name, sub: "Debt payment", amount: -p.amount });
  rows.sort((x, y) => dateOrd(y.date) - dateOrd(x.date));

  const totalIn = rows.reduce((s, r) => (r.amount > 0 ? s + r.amount : s), 0);
  const totalOut = rows.reduce((s, r) => (r.amount < 0 ? s - r.amount : s), 0);
  const typeLabel = a.type === "ewallet" ? "e-wallet" : a.type;

  const summ = (label: string, value: string, color?: string) => (
    <div className="finance-detail-metric"><span>{label}</span><strong style={{ color: color || "var(--ink)" }}>{value}</strong></div>
  );

  return (
    <Page back="Back to Finances" onBack={st.closeAccount2}>
      <Card className="detail-stack">
        <div className="finance-detail-heading">
          <span className="finance-detail-icon" style={{ background: m.tint, color: m.color }}><i aria-hidden="true" className={m.fa} /></span>
          <div className="finance-detail-name"><h1 className="detail-heading detail-title">{a.name}</h1><Badge style={{ color: m.color }}>{typeLabel}{a.apr ? ` · ${a.apr}% p.a.` : ""}</Badge></div>
        </div>
        <div className="finance-detail-total"><span>Balance</span><strong>{peso(a.balance)}</strong></div>
        <div className="finance-detail-metrics">{summ("In", "+" + peso(totalIn), "var(--green-ink)")}{summ("Out", "−" + peso(totalOut), "var(--orange-ink)")}{summ("Movements", String(rows.length))}</div>
      </Card>
      <Card title="In & out">
        {rows.length > 0 ? rows.map((r, i) => (
          <div key={i} className="account-ledger-row" style={{ borderTop: i ? "1px solid var(--line)" : "none" }}>
            <span className="finance-detail-date">{r.date}</span>
            <span className="account-ledger-label finance-ledger-copy"><strong>{r.label}</strong><Badge className="finance-ledger-category">{r.sub}</Badge></span>
            <span className="account-ledger-amount finance-detail-money" style={{ color: r.amount >= 0 ? "var(--green-ink)" : "var(--orange-ink)" }}>{r.amount >= 0 ? "+" : "−"}{peso(Math.abs(r.amount))}</span>
          </div>
        )) : <p className="detail-copy">No money in or out of this account yet.</p>}
      </Card>
    </Page>
  );
}
